import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  getUpgradeWallBannerText,
  getUpgradeWallValueHeading,
  UpgradeWallInvestmentCount,
} from '../upgrade-wall.ts';

export interface UpgradeWallModalProps {
  open: boolean;
  onClose: () => void;
  onViewPlans: () => void;
  investmentCount: UpgradeWallInvestmentCount;
}

const valueItems = [
  'Registrar todas tus inversiones en un solo sitio',
  'Ver la rentabilidad real de toda tu cartera',
  'Controlar ingresos, gastos y documentos de cada una',
  'Pedir análisis y recomendaciones a tu asesor de IA',
];

const UpgradeWallModal = ({
  open,
  onClose,
  onViewPlans,
  investmentCount,
}: UpgradeWallModalProps) => {
  const theme = useTheme();
  const personalizationBanner = getUpgradeWallBannerText(investmentCount);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: 440,
          borderRadius: 2,
          p: 0,
          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.18)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.tokens.neutrals[500], 0.46),
        },
      }}
    >
      <DialogContent sx={{ px: 3, py: 3.5 }}>
        <Stack spacing={2.5}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                backgroundColor: theme.palette.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ApartmentOutlinedIcon sx={{ color: theme.palette.primary.main }} />
            </Box>

            <IconButton
              aria-label="Cerrar"
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                mt: -0.5,
                mr: -1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 500, color: 'text.primary', mb: 1 }}>
              Tienes más inversiones. Gestiónalas todas.
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
              Tu primera inversión es gratis. Para gestionar toda tu cartera desde un solo sitio,
              activa un plan.
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.primary[50],
              borderRadius: '12px',
              px: '18px',
              py: 2,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
              {getUpgradeWallValueHeading(investmentCount)}
            </Typography>

            <Stack spacing={1}>
              {valueItems.map((item) => (
                <Box key={item} display="flex" alignItems="flex-start" gap={1}>
                  <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 16, mt: '2px' }} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {personalizationBanner ? (
            <Box
              sx={{
                backgroundColor: theme.palette.warning[50],
                borderRadius: '8px',
                px: '14px',
                py: '10px',
                border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.palette.warning.dark ?? theme.palette.warning.main,
                  lineHeight: 1.5,
                }}
              >
                {personalizationBanner}
              </Typography>
            </Box>
          ) : null}

          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.45 }}>
            Si prefieres no activar un plan, tu cuenta sigue siendo gratuita con tu primera
            inversión.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              mt: '20px',
            }}
          >
            <Button
              fullWidth
              onClick={onClose}
              variant="outlined"
              size="medium"
              sx={{
                minHeight: 38,
                py: 0.75,
                borderColor: 'divider',
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                color: 'text.primary',
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'text.secondary',
                  backgroundColor: alpha(theme.palette.tokens.neutrals[100], 0.6),
                },
              }}
            >
              Ahora no
            </Button>

            <Button
              fullWidth
              onClick={onViewPlans}
              variant="contained"
              size="medium"
              sx={{
                minHeight: 38,
                py: 0.75,
                borderRadius: '8px',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Ver planes desde 19€/mes
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeWallModal;
